import { useEffect, useRef, useState, type ReactNode } from 'react';
import { type Control, Controller, get, useFormContext } from 'react-hook-form';
import Editor, { type ContentEditableEvent } from 'react-simple-wysiwyg';
import { HexColorPicker } from 'react-colorful';
import { usePathContext } from 'engine/pathContext';
import { Tooltip } from './Tooltip';

type FieldContainerProps = {
	compact?: boolean;
	name: string;
	children: ReactNode;
};

const defaultClasses = `group-data-[invalid]:ring-danger-500 group-data-[invalid]:bg-danger-50 block w-full border bg-white text-gray-900 placeholder:text-gray-400 focus:shadow-[_0px_0px_0px_2px_#ffffff,0px_0px_0px_5px_var(--theme-transparent)]`;
export const defaultInputClasses = defaultClasses + ' read-only:bg-gray-100 read-only:placeholder:text-gray-700';

export const sizes: Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', string> = {
	xs: 'rounded-md p-1 text-xs leading-5',
	sm: 'rounded-md p-1.5 text-sm leading-5',
	md: 'rounded-lg p-3 text-base leading-5',
	lg: 'rounded-xl px-5 py-4 text-lg leading-6',
	xl: 'rounded-xl px-6 py-5 text-xl leading-6',
};

export const modes: Record<'default' | 'warning' | 'error', string> = {
	default: 'border-gray-400 hover:border-theme-500 active:border-theme-500',
	warning: 'border-yellow-500 hover:border-yellow-600 active:border-yellow-600',
	error: 'border-red-500 hover:border-red-600 active:border-red-600',
};

export function FieldContainer({ compact, name, children }: FieldContainerProps) {
	return (
		<div data-form-field={name} className={`group ${compact ? 'flex flex-col gap-2 md:flex-row md:items-center' : 'w-full'}`}>
			{children}
		</div>
	);
}

export function FieldLabel({
	label,
	labelWidth,
	name,
	info,
}: {
	name: string;
	label: React.ReactNode;
	labelWidth?: string;
	info?: string;
}) {
	if (typeof label !== 'string') return label;

	return (
		<label
			htmlFor={name + '-field'}
			className="relative inline-flex items-center text-sm font-medium leading-6 text-gray-900 group-data-[invalid]:text-danger-900 dark:text-gray-100"
			style={labelWidth ? { width: labelWidth } : undefined}
		>
			<span>{label}</span>
			{info && <Tooltip info={info} />}
		</label>
	);
}

export function FieldValidationContainer({ error }: { error: string }) {
	return <div className="mt-1 p-1 text-xs leading-none text-danger-700">{error}</div>;
}

export type TextFieldProps = {
	name: string;
	label: string | React.ReactNode;
	placeholder?: string;
	compact?: boolean;
	type?: string;
	size?: number;
	labelWidth?: string;
	readonly?: boolean;
	disabled?: boolean;
	optional?: boolean;
	hideLabel?: boolean;
	value?: string;
	fieldSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
	mode?: 'default' | 'warning' | 'error';
	className?: string;
	autoFocus?: boolean;
	info?: string;
	onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};
export function TextField({
	name,
	label,
	placeholder,
	compact,
	optional,
	hideLabel,
	type = 'text',
	size,
	labelWidth,
	readonly,
	disabled,
	value,
	fieldSize = 'md',
	mode = 'default',
	className = '',
	autoFocus,
	info,
	onKeyDown,
}: TextFieldProps) {
	const {
		register,
		formState: { errors },
	} = useFormContext();

	const path = usePathContext(name);

	const err = get(errors, path);

	return (
		<FieldContainer compact={compact} name={path}>
			{!hideLabel && <FieldLabel label={label} labelWidth={labelWidth} name={path} info={info} />}
			<div className="relative mt-1 grow">
				<input
					id={`${path}-field`}
					type={type}
					autoComplete="off"
					readOnly={readonly}
					disabled={disabled}
					placeholder={placeholder}
					className={`${sizes[fieldSize]} ${modes[mode]} ${className} ${defaultInputClasses}`}
					size={size}
					value={value}
					autoFocus={autoFocus}
					onKeyDown={onKeyDown}
					{...register(path, {
						setValueAs: (v) => (optional && !v.trim() ? undefined : v),
					})}
				/>
				{err && <FieldValidationContainer error={err.message} />}
			</div>
		</FieldContainer>
	);
}

// type NumberFieldProps = Omit<TextFieldProps, 'type'>;

export type NumberFieldProps = {
	name: string;
	label: string | React.ReactNode;
	placeholder?: string;
	compact?: boolean;
	size?: number;
	labelWidth?: string;
	readonly?: boolean;
	disabled?: boolean;
	hideLabel?: boolean;
	defaultValue?: number;
	min?: number;
	max?: number;
	step?: number | 'any';
	fieldSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
	mode?: 'default' | 'warning' | 'error';
	className?: string;
};
export function NumberField({
	name,
	label,
	placeholder,
	compact,
	size,
	labelWidth,
	readonly,
	disabled,
	hideLabel,
	defaultValue,
	min,
	max,
	step,
	fieldSize = 'md',
	mode = 'default',
	className = '',
}: NumberFieldProps) {
	const {
		register,
		formState: { errors },
	} = useFormContext();

	const path = usePathContext(name);
	const err = get(errors, path);

	return (
		<FieldContainer compact={compact} name={path}>
			{!hideLabel && <FieldLabel label={label} labelWidth={labelWidth} name={path} />}
			<div className="relative mt-1 grow">
				<input
					id={`${path}-field`}
					type="number"
					autoComplete="off"
					readOnly={readonly}
					disabled={disabled}
					placeholder={placeholder}
					className={`${defaultInputClasses} ${sizes[fieldSize]} ${modes[mode]} ${className}`}
					size={size}
					min={min}
					max={max}
					step={step}
					defaultValue={defaultValue}
					{...register(path, {
						setValueAs: (v) => (v === '' ? undefined : Number(v)),
					})}
				/>
				{err && <FieldValidationContainer error={err.message} />}
			</div>
		</FieldContainer>
	);
}

export type SelectFieldProps = {
	name: string;
	label: string | React.ReactNode;
	options: { value: string; label: string }[];
	placeholder?: string;
	compact?: boolean;
	labelWidth?: string;
	disabled?: boolean;
	optional?: boolean;
	hideLabel?: boolean;
	fieldSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
	mode?: 'default' | 'warning' | 'error';
	className?: string;
	info?: string;
};

export function SelectField({
	name,
	label,
	hideLabel,
	options,
	placeholder,
	compact,
	labelWidth,
	disabled,
	control,
	optional,
	fieldSize = 'md',
	mode = 'default',
	className = '',
	info,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
}: SelectFieldProps & { control?: Control<any, any, any> }) {
	const {
		formState: { errors },
		register,
	} = useFormContext();

	const path = usePathContext(name);

	const err = get(errors, path);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const fieldRenderer = (ctx?: { field: any }) => {
		return (
			<select
				id={`${path}-field`}
				disabled={disabled}
				className={`${defaultClasses} ${sizes[fieldSize]} ${modes[mode]} ${className} disabled:bg-gray-100 disabled:placeholder:text-gray-700`}
				{...(control
					? ctx?.field
					: register(path, {
							setValueAs: (v) => (v === '' ? undefined : v),
						}))}
			>
				{optional && <option value="">None</option>}
				{placeholder && (
					<option value="" disabled>
						{placeholder}
					</option>
				)}
				{options.map(({ value, label }) => (
					<option key={value} value={value}>
						{label}
					</option>
				))}
			</select>
		);
	};

	return (
		<FieldContainer compact={compact} name={path}>
			{!hideLabel && <FieldLabel label={label} labelWidth={labelWidth} name={path} info={info} />}
			<div className="relative mt-1 grow">
				{!control && fieldRenderer()}
				{control && <Controller control={control} name={path} render={fieldRenderer} />}
				{err && <FieldValidationContainer error={err.message} />}
			</div>
		</FieldContainer>
	);
}

export type RichTextFieldProps = {
	name: string;
	label: string | React.ReactNode;
	hideLabel?: boolean;
	placeholder?: string;
	labelWidth?: string;
	readonly?: boolean;
	optional?: boolean;
	defaultValue?: string;
	info?: string;
};
export function RichTextField({ name, optional, label, hideLabel, placeholder, readonly, defaultValue, info }: RichTextFieldProps) {
	const {
		formState: { errors },
		getValues,
		setValue,
		trigger,
	} = useFormContext();

	const path = usePathContext(name);

	const [html, setHtml] = useState((getValues(path) || defaultValue) ?? '');

	const err = get(errors, path);
	function onChange(e: ContentEditableEvent) {
		const newValue = e.target.value.trim();
		setHtml(newValue);

		// if this field is optional and the content is effectively empty
		// we want to return undefined instead
		const valueOrUndefined = optional && (!newValue.trim() || newValue === '<br>') ? undefined : newValue;
		setValue(path, valueOrUndefined);
		trigger(path);
	}

	return (
		<FieldContainer compact={false} name={path}>
			{!hideLabel && <FieldLabel label={label} name={path} info={info} />}
			<div data-readonly={readonly ? '' : undefined} className="group relative mt-1 grow data-[readonly]:pointer-events-none">
				<Editor
					disabled={readonly}
					onPaste={function (e) {
						e.preventDefault();
						const text = e.clipboardData.getData('text/plain');
						document.execCommand('insertText', false, text);
					}}
					placeholder={placeholder}
					contentEditable={!readonly}
					style={{ opacity: readonly ? '.6' : '1' }}
					value={html}
					onChange={onChange}
					containerProps={{ className: 'bg-white' }}
				/>
				{err && <FieldValidationContainer error={err.message} />}
			</div>
		</FieldContainer>
	);
}

export type CheckboxFieldProps = {
	name: string;
	label: string | React.ReactNode;
	hideLabel?: boolean;
	compact?: boolean;
	labelWidth?: string;
	readonly?: boolean;
	title?: string;
	info?: string;
};

export function CheckboxField({ name, label, hideLabel, compact, labelWidth, readonly, title, info }: CheckboxFieldProps) {
	const {
		register,
		formState: { errors },
	} = useFormContext();

	const path = usePathContext(name);
	const err = get(errors, path);

	return (
		<FieldContainer compact={compact} name={path}>
			{!hideLabel && <FieldLabel label={label} labelWidth={labelWidth} name={path} info={info} />}
			<input //
				title={title}
				id={`${path}-field`}
				type="checkbox"
				className=""
				disabled={readonly}
				{...register(path)}
			/>
			<FieldValidationContainer error={err?.message} />
		</FieldContainer>
	);
}

export function ToggleField({ name, label, hideLabel, compact, labelWidth, readonly, title }: CheckboxFieldProps) {
	const {
		register,
		formState: { errors },
	} = useFormContext();

	const path = usePathContext(name);
	const err = get(errors, path);

	return (
		<FieldContainer compact={compact} name={name}>
			{!hideLabel && <FieldLabel label={label} labelWidth={labelWidth} name={path} />}
			<div className="relative mt-1">
				<label className="toggle">
					<input title={title} id={`${path}-field`} type="checkbox" className="" disabled={readonly} {...register(path)} />
					<span className="toggle-view"></span>
				</label>
				<FieldValidationContainer error={err?.message} />
			</div>
		</FieldContainer>
	);
}

type ColorFieldProps = {
	name: string;
	label: string | React.ReactNode;
	hideLabel?: boolean;
	placeholder?: string;
	compact?: boolean;
	size?: number;
	labelWidth?: string;
	readonly?: boolean;
	palette?: string[];
};

export function ColorField(props: ColorFieldProps) {
	const { name, label, hideLabel, placeholder, compact, size, labelWidth, readonly, palette } = props;

	const {
		register,
		setValue,
		watch,
		formState: { errors },
	} = useFormContext();

	const path = usePathContext(name);
	const err = get(errors, path);
	const color = watch(path);
	const [open, setOpen] = useState(false);
	const pickerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (!pickerRef.current?.contains(e.target as Node)) {
				setOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	return (
		<FieldContainer compact={compact} name={path}>
			{!hideLabel && <FieldLabel label={label} labelWidth={labelWidth} name={path} />}
			<div className="relative mt-1 grow">
				<div className="flex overflow-hidden rounded-md border border-gray-300 dark:border-gray-700">
					<button type="button" disabled={readonly} onClick={() => setOpen(!open)} className="checkerboard w-10 shrink-0">
						<div className="size-full" style={{ backgroundColor: color || '#000000' }}></div>
					</button>
					<input
						id={`${path}-field`}
						type="text"
						autoComplete="off"
						readOnly={readonly}
						placeholder={placeholder}
						className={
							'block w-full border-0 border-l border-gray-300 px-2 py-1.5 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-theme-600 focus-visible:bg-gray-50 focus-visible:outline-none dark:border-gray-700 ' +
							'dark:focus:ring-theme-700/80 dark:bg-gray-900 dark:text-gray-100 dark:focus-visible:bg-gray-950 sm:text-sm sm:leading-6'
						}
						size={size}
						{...register(path)}
					/>
				</div>
				{open && (
					<div ref={pickerRef} className="absolute z-10 mt-2 rounded-[10px] border border-gray-300 bg-white shadow-lg">
						<HexColorPicker color={color || '#000000'} onChange={(val) => setValue(path, val, { shouldValidate: true })} />
						{palette && (
							<div className="flex flex-wrap gap-1 px-2 py-1">
								{palette.map((p) => (
									<div
										onClick={() => setValue(path, p)}
										className="checkerboard size-5 cursor-pointer overflow-hidden rounded-full border border-gray-300"
									>
										<div className="size-full" style={{ backgroundColor: p }}></div>
									</div>
								))}
							</div>
						)}
					</div>
				)}
				{err && <FieldValidationContainer error={err.message} />}
			</div>
		</FieldContainer>
	);
}

export type DateTimeFieldProps = {
	name: string;
	label: string | React.ReactNode;
	placeholder?: string;
	compact?: boolean;
	size?: number;
	labelWidth?: string;
	readonly?: boolean;
	disabled?: boolean;
	optional?: boolean;
	hideLabel?: boolean;
	value?: string;
	fieldSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
	mode?: 'default' | 'warning' | 'error';
	className?: string;
	autoFocus?: boolean;
	type?: 'datetime-local' | 'date' | 'time';
	onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};
export function DateTimeField({
	name,
	label,
	placeholder,
	compact,
	hideLabel,
	labelWidth,
	readonly,
	disabled,
	fieldSize = 'md',
	mode = 'default',
	className = '',
	autoFocus,
	type = 'datetime-local',
}: DateTimeFieldProps) {
	const {
		control,
		formState: { errors },
		// watch,
	} = useFormContext();

	const path = usePathContext(name);

	const err = get(errors, path);

	function encodeLocalDateTime(dateTimeStr: string): string | null {
		try {
			const date = new Date(dateTimeStr);
			return date.toISOString();
		} catch (e) {
			console.error('error encoding local date time', e);
			return null;
		}
	}

	function decodeLocalDateTime(isoStr: string): string {
		const date = new Date(isoStr);
		const pad = (n: number) => n.toString().padStart(2, '0');
		switch (type) {
			case 'date':
				return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
			case 'time':
				return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
			default: // datetime-local
				return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
		}
	}

	const inputType = type === 'date' ? 'date' : type === 'time' ? 'time' : 'datetime-local';

	return (
		<FieldContainer compact={compact} name={path}>
			{!hideLabel && <FieldLabel label={label} labelWidth={labelWidth} name={path} />}
			<div className="relative mt-1 grow">
				{/* for debugging: */}
				{/* <div className="bg-red-600 p-4 text-white">watched value:{v}</div> */}
				<Controller
					name={path}
					control={control}
					render={({ field }) => (
						<input
							{...field}
							type={inputType}
							value={field.value ? decodeLocalDateTime(field.value) : ''}
							onChange={(e) => {
								console.log('gonna set value', e.target.value, 'encoded', encodeLocalDateTime(e.target.value));
								return field.onChange(encodeLocalDateTime(e.target.value));
							}}
							className={`${sizes[fieldSize]} ${modes[mode]} ${className} ${defaultInputClasses}`}
							placeholder={placeholder}
							readOnly={readonly}
							disabled={disabled}
							autoFocus={autoFocus}
						/>
					)}
				/>

				{err && <FieldValidationContainer error={err.message} />}
			</div>
		</FieldContainer>
	);
}
